import { useState, useRef } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { AdminRecord } from '../types';
import { ADMIN_ROLES, roleLabel, roleColor } from '../types';

interface Props {
  admin: AdminRecord;
  isSelf: boolean;
  onUpdate: () => void;
}

export default function AdminDetailHeader({ admin, isSelf, onUpdate }: Props) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const displayName = admin.full_name || admin.email;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `${admin.userId}/avatar-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      await supabase.from('profiles').update({ avatar_url: urlData.publicUrl, updated_at: new Date().toISOString() }).eq('id', admin.userId);
      await onUpdate();
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Erro ao enviar foto');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeAvatar = async () => {
    setUploading(true);
    await supabase.from('profiles').update({ avatar_url: null, updated_at: new Date().toISOString() }).eq('id', admin.userId);
    await onUpdate();
    setUploading(false);
  };

  const fmtRelative = (d: string | null) => {
    if (!d) return 'Nunca';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min atrás`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h atrás`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d atrás`;
    return new Date(d).toLocaleDateString('pt-BR');
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-start gap-6">
        {/* Avatar */}
        <div className="relative shrink-0">
          <div className="w-20 h-20 rounded-2xl overflow-hidden bg-violet-600/20 flex items-center justify-center">
            {admin.avatar_url ? (
              <img src={admin.avatar_url} alt={displayName} className="w-20 h-20 object-cover" />
            ) : (
              <span className="text-[28px] font-700 text-violet-300">{displayName[0]?.toUpperCase() || 'A'}</span>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <i className="ri-loader-4-line text-white animate-spin text-xl" />
              </div>
            )}
          </div>
          <div className="flex gap-1 mt-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex-1 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-[10px] text-white/50 hover:text-white cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-upload-2-line mr-0.5" />{admin.avatar_url ? 'Trocar' : 'Foto'}
            </button>
            {admin.avatar_url && (
              <button
                onClick={removeAvatar}
                disabled={uploading}
                className="py-1 px-2 bg-white/5 hover:bg-red-500/10 border border-white/10 rounded-lg text-[10px] text-white/40 hover:text-red-400 cursor-pointer transition-colors"
              >
                <i className="ri-delete-bin-line" />
              </button>
            )}
          </div>
          {uploadError && <p className="text-[10px] text-red-400 mt-1 w-20 break-words">{uploadError}</p>}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-[22px] font-700 text-white leading-tight">{displayName}</h2>
              <p className="text-white/50 text-[14px] mt-0.5">{admin.email}</p>
              {admin.phone && <p className="text-white/40 text-[13px] mt-0.5"><i className="ri-phone-line mr-1.5" />{admin.phone}</p>}
            </div>
            {isSelf && (
              <div className="px-2.5 py-1 bg-violet-600/20 border border-violet-500/30 rounded-lg text-[11px] text-violet-300 shrink-0">
                <i className="ri-user-line mr-1" />Você
              </div>
            )}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className={`text-[12px] font-600 px-3 py-1 rounded-full ${roleColor[admin.role] || 'bg-white/10 text-white/40'}`}>
              <i className="ri-shield-user-line mr-1.5" />{roleLabel[admin.role] || admin.role}
            </span>
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5">
              <div className={`w-1.5 h-1.5 rounded-full ${admin.status === 'active' ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className={`text-[12px] font-600 ${admin.status === 'active' ? 'text-emerald-400' : 'text-red-400'}`}>
                {admin.status === 'active' ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            {admin.last_seen_at && (
              <span className="text-[12px] text-white/30 px-3 py-1 rounded-full bg-white/5">
                <i className="ri-time-line mr-1.5" />Último acesso: {fmtRelative(admin.last_seen_at)}
              </span>
            )}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { label: 'Adicionado em', val: admin.createdAt ? new Date(admin.createdAt).toLocaleDateString('pt-BR') : '—' },
              { label: 'CPF', val: admin.cpf || '—' },
              { label: 'Cidade', val: admin.city ? `${admin.city}${admin.state ? '/' + admin.state : ''}` : '—' },
            ].map((item) => (
              <div key={item.label} className="bg-white/3 rounded-xl px-3 py-2">
                <p className="text-[10px] text-white/30 uppercase tracking-wider">{item.label}</p>
                <p className="text-[13px] text-white mt-0.5 truncate">{item.val}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
